import {
  ICE_RESTART_DEBOUNCE_MS,
  IceRecoveryLadder,
  type IceDeathState,
  type IceReportDecision,
  type IceRestartDecision,
} from 'utils/webrtc/IceRecoveryLadder';

export interface IceDeathHost {
  // The host's failure is already reported (or the host is gone): no death is left to act on.
  isSettled(): boolean;
  // ICE up but DTLS never completed: a restart cannot help, so the host may act here (rebuild).
  dtlsNeverCompleted(state: IceDeathState): boolean;
  onReport(decision: IceReportDecision, state: IceDeathState): void;
  onRestart(
    decision: IceRestartDecision,
    state: IceDeathState,
    diedAt: number
  ): void | Promise<void>;
}

// Decides, per peer connection, whether an ICE death is reported or restarted after a debounce.
export class IceDeathSequencer {
  private currentLadder = new IceRecoveryLadder();

  private restartTimer?: number;

  constructor(private readonly host: IceDeathHost) {}

  get ladder(): IceRecoveryLadder {
    return this.currentLadder;
  }

  onIceConnected() {
    // A rung is spent only by a restart that fired; cancelling a pending one must hand it back.
    if (this.restartTimer !== undefined) {
      this.currentLadder.forgiveUndispatchedDeath();
    }

    this.clearRestartTimer();
    this.currentLadder.markConnected();
  }

  onIceDeath(state: IceDeathState, previouslyConnected: boolean) {
    if (this.host.isSettled()) {
      return;
    }

    // A pending restart already owns this death; `disconnected` → `failed`
    // for one outage must not walk the ladder twice.
    if (this.restartTimer !== undefined) {
      return;
    }

    if (this.host.dtlsNeverCompleted(state)) {
      return;
    }

    const diedAt = Date.now();
    const decision = this.currentLadder.decide(state, previouslyConnected, diedAt);

    if (decision.action === 'report') {
      this.host.onReport(decision, state);
      return;
    }

    this.restartTimer = window.setTimeout(() => {
      this.restartTimer = undefined;

      if (this.host.isSettled()) {
        return;
      }

      this.host.onRestart(decision, state, diedAt);
    }, ICE_RESTART_DEBOUNCE_MS);
  }

  reset() {
    this.clearRestartTimer();
    this.currentLadder = new IceRecoveryLadder();
  }

  // Not terminal: a host drops a pending restart mid-life (a DTLS rebuild, a handle cleanup)
  // and keeps sequencing the deaths that follow.
  dispose() {
    this.clearRestartTimer();
  }

  private clearRestartTimer() {
    window.clearTimeout(this.restartTimer);
    this.restartTimer = undefined;
  }
}
